import { useEffect, useState } from "react";
import { api, getErrorMessage, formatCurrency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Crown, Building2, Users, TrendingUp, AlertTriangle, Loader2,
  PauseCircle, PlayCircle, Trash2, Edit3,
} from "lucide-react";

const PLAN_LABELS = { trial: "Essai", starter: "Starter", enterprise: "Enterprise", unlimited: "Gratuit illimité" };
const STATUS_LABELS = {
  trialing: "Essai",
  active: "Actif",
  past_due: "En retard",
  suspended: "Suspendu",
  canceled: "Annulé",
};
const STATUS_COLORS = {
  trialing: "bg-amber-100 text-amber-800 border-amber-300",
  active: "bg-emerald-100 text-emerald-800 border-emerald-300",
  past_due: "bg-orange-100 text-orange-800 border-orange-300",
  suspended: "bg-red-100 text-red-800 border-red-300",
  canceled: "bg-gray-100 text-gray-800 border-gray-300",
};

const Kpi = ({ label, value, icon: Icon, accent }) => (
  <Card className="overflow-hidden" data-testid={`kpi-${label}`}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${accent || "bg-indigo-100"}`}>
        <Icon className="h-5 w-5 text-indigo-700" strokeWidth={1.5} />
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold truncate">{value}</p>
      </div>
    </CardContent>
  </Card>
);

export default function SuperAdminPage() {
  const { isAdmin, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planEditTarget, setPlanEditTarget] = useState(null);
  const [planEditValue, setPlanEditValue] = useState("starter");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmPhrase, setConfirmPhrase] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [orgs, statsRes, funnelRes] = await Promise.all([
        api.superAdminListOrgs(),
        api.superAdminStats(),
        api.getFunnel().catch(() => ({ data: null })),
      ]);
      setData(orgs.data);
      setStats(statsRes.data);
      setFunnel(funnelRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Chargement impossible"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchAll();
  }, [isAdmin]);

  if (authLoading) return null;
  if (!isAdmin) return <Navigate to="/" replace />;

  const handleSuspend = async (org) => {
    const reason = window.prompt(`Raison de la suspension de "${org.name}" ?`, "");
    if (reason === null) return;
    try {
      await api.superAdminSuspendOrg(org.id, reason);
      toast.success(`${org.name} suspendue`);
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleReactivate = async (org) => {
    if (!window.confirm(`Réactiver "${org.name}" ?`)) return;
    try {
      await api.superAdminReactivateOrg(org.id);
      toast.success(`${org.name} réactivée`);
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleChangePlan = async () => {
    try {
      await api.superAdminChangePlan(planEditTarget.id, planEditValue);
      toast.success(`Plan ${PLAN_LABELS[planEditValue]} appliqué à ${planEditTarget.name}`);
      setPlanEditTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    try {
      await api.superAdminDeleteOrg(deleteTarget.id, confirmPhrase);
      toast.success(`${deleteTarget.name} supprimée`);
      setDeleteTarget(null);
      setConfirmPhrase("");
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="super-admin-page">
      <div className="flex items-center gap-3">
        <Crown className="h-8 w-8 text-amber-600" />
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Super-Admin</h1>
          <p className="text-muted-foreground text-sm">Vue globale Bati Assistant — réservée à l'équipe plateforme.</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi label="Organisations" value={data?.total_orgs ?? "—"} icon={Building2} />
        <Kpi label="Membres actifs" value={data?.total_active_members ?? "—"} icon={Users} />
        <Kpi
          label="MRR estimé"
          value={data ? formatCurrency(data.estimated_mrr_eur) : "—"}
          icon={TrendingUp}
          accent="bg-emerald-100"
        />
        <Kpi
          label="ARR estimé"
          value={data ? formatCurrency(data.estimated_arr_eur) : "—"}
          icon={TrendingUp}
          accent="bg-amber-100"
        />
      </div>

      {/* Mini-stats */}
      {stats && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Vue d'ensemble</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-xs uppercase text-muted-foreground">Plans</p>
                <ul className="mt-1 space-y-0.5">
                  {Object.entries(stats.orgs_by_plan).map(([p, n]) => (
                    <li key={p}>{PLAN_LABELS[p] || p} : <strong>{n}</strong></li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs uppercase text-muted-foreground">Statuts</p>
                <ul className="mt-1 space-y-0.5">
                  {Object.entries(stats.orgs_by_subscription_status).map(([s, n]) => (
                    <li key={s}>{STATUS_LABELS[s] || s} : <strong>{n}</strong></li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs uppercase text-muted-foreground">Utilisateurs</p>
                <p className="mt-1"><strong>{stats.users_total}</strong> au total</p>
                <p><strong>{stats.active_memberships}</strong> memberships actifs</p>
              </div>
              <div>
                <p className="text-xs uppercase text-muted-foreground">Paiements</p>
                <p className="mt-1"><strong>{stats.payments_applied}</strong> appliqués</p>
                <p className="text-xs">Recettes : <strong>{formatCurrency(stats.total_revenue_eur)}</strong></p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Conversion funnel */}
      {funnel && (
        <Card data-testid="funnel-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Funnel de conversion (essai → paiement)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3 rounded-md bg-blue-50 border border-blue-200">
                <p className="text-xs uppercase text-blue-800">Vues du dialog</p>
                <p className="text-2xl font-bold">{funnel.funnel.viewed_dialog}</p>
                <p className="text-xs text-muted-foreground">utilisateurs uniques</p>
              </div>
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200">
                <p className="text-xs uppercase text-amber-800">Clics "Payer"</p>
                <p className="text-2xl font-bold">{funnel.funnel.clicked_pay}</p>
                <p className="text-xs text-muted-foreground">{funnel.funnel.view_to_click_pct}% des vues</p>
              </div>
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
                <p className="text-xs uppercase text-emerald-800">Paiements réussis</p>
                <p className="text-2xl font-bold">{funnel.funnel.completed_purchase}</p>
                <p className="text-xs text-muted-foreground">{funnel.funnel.click_to_pay_pct}% des clics · {funnel.funnel.view_to_pay_pct}% des vues</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orgs table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Toutes les organisations</span>
            <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-orgs">
              {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Actualiser
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-2">Nom</th>
                    <th className="py-2 pr-2">Plan</th>
                    <th className="py-2 pr-2">Statut</th>
                    <th className="py-2 pr-2 text-right">Utilisateurs</th>
                    <th className="py-2 pr-2 text-right">Membres</th>
                    <th className="py-2 pr-2 text-right">MRR</th>
                    <th className="py-2 pr-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.organizations || []).map((org) => (
                    <tr key={org.id} className="border-b hover:bg-muted/30" data-testid={`org-row-${org.id}`}>
                      <td className="py-2 pr-2">
                        <div className="font-medium">{org.name}</div>
                        <div className="text-xs text-muted-foreground">{org.billing_email}</div>
                      </td>
                      <td className="py-2 pr-2">
                        <Badge variant="outline">{PLAN_LABELS[org.plan] || org.plan}</Badge>
                      </td>
                      <td className="py-2 pr-2">
                        <Badge className={`border ${STATUS_COLORS[org.subscription_status] || ""}`} variant="outline">
                          {STATUS_LABELS[org.subscription_status] || org.subscription_status}
                        </Badge>
                      </td>
                      <td className="py-2 pr-2 text-right">{org.slots_purchased}</td>
                      <td className="py-2 pr-2 text-right">{org.active_members}</td>
                      <td className="py-2 pr-2 text-right font-medium">{formatCurrency(org.mrr_eur || 0)}</td>
                      <td className="py-2 pr-2 text-right">
                        <div className="inline-flex gap-1">
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8"
                            title="Changer le plan"
                            onClick={() => { setPlanEditTarget(org); setPlanEditValue(org.plan || "starter"); }}
                            data-testid={`edit-plan-${org.id}`}
                          >
                            <Edit3 className="h-4 w-4 text-indigo-600" />
                          </Button>
                          {org.subscription_status === "suspended" ? (
                            <Button
                              variant="ghost" size="icon" className="h-8 w-8"
                              title="Réactiver"
                              onClick={() => handleReactivate(org)}
                              data-testid={`reactivate-${org.id}`}
                            >
                              <PlayCircle className="h-4 w-4 text-emerald-600" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost" size="icon" className="h-8 w-8"
                              title="Suspendre"
                              onClick={() => handleSuspend(org)}
                              data-testid={`suspend-${org.id}`}
                            >
                              <PauseCircle className="h-4 w-4 text-amber-600" />
                            </Button>
                          )}
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50"
                            title="Supprimer"
                            onClick={() => { setDeleteTarget(org); setConfirmPhrase(""); }}
                            data-testid={`delete-org-${org.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change plan dialog */}
      <Dialog open={!!planEditTarget} onOpenChange={(o) => !o && setPlanEditTarget(null)}>
        <DialogContent data-testid="plan-edit-dialog">
          <DialogHeader>
            <DialogTitle>Changer le plan de {planEditTarget?.name}</DialogTitle>
            <DialogDescription>
              Le changement de plan est immédiat et se répercute sur le calcul du MRR.
            </DialogDescription>
          </DialogHeader>
          <Select value={planEditValue} onValueChange={setPlanEditValue}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="trial">Trial (essai 30j)</SelectItem>
              <SelectItem value="starter">Starter (79€/mois + 15€/utilisateur)</SelectItem>
              <SelectItem value="enterprise">Enterprise (devis)</SelectItem>
              <SelectItem value="unlimited" data-testid="plan-option-unlimited">
                Gratuit illimité (interne · sans facturation, utilisateurs illimités)
              </SelectItem>
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanEditTarget(null)}>Annuler</Button>
            <Button onClick={handleChangePlan} data-testid="confirm-change-plan">Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-org-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Supprimer {deleteTarget?.name} ?
            </DialogTitle>
            <DialogDescription>
              Cette action supprime l'organisation, ses memberships, invitations et abonnements.
              Les données métier (commandes, clients, salariés) <strong>ne sont pas supprimées</strong> mais deviendront orphelines.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>Pour confirmer, tapez exactement :</p>
            <code className="block bg-red-50 border border-red-200 rounded px-2 py-1 text-red-900 font-mono">
              DELETE ORG {deleteTarget?.slug || deleteTarget?.id}
            </code>
            <Input
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              placeholder="Tapez la phrase de confirmation"
              data-testid="delete-org-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annuler</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!confirmPhrase}
              data-testid="confirm-delete-org"
            >
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
